import {
  DataGrid,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
  GridFooterContainer,
  GridPagination,
  GridPaginationModel,
  GridRowSelectionModel,
  gridPaginationModelSelector,
  gridRowCountSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import { Box, Typography } from '@mui/material';
import { DeletedDriveFolderRow } from '../../types/softDelete.types';

function RecycleBinFoldersTableFooter() {
  const apiRef = useGridApiContext();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);
  const from = rowCount === 0 ? 0 : paginationModel.page * paginationModel.pageSize + 1;
  const to = Math.min((paginationModel.page + 1) * paginationModel.pageSize, rowCount);
  return (
    <GridFooterContainer
      sx={{
        px: 2,
        borderTop: '1px solid #e5e7eb',
        backgroundColor: 'rgba(249,250,251,0.5)',
        '& .MuiTablePagination-selectLabel': { mb: 0 },
        '& .MuiTablePagination-displayedRows': { mb: 0 },
        '& .MuiTablePagination-select': {
          paddingTop: 0,
          paddingBottom: 0,
          lineHeight: '1.5',
        },
      }}
    >
      <Typography sx={{ color: '#6b7280', alignSelf: 'center' }}>
        Showing{' '}
        <strong style={{ color: '#111827' }}>{from}</strong> to{' '}
        <strong style={{ color: '#111827' }}>{to}</strong> of{' '}
        <strong style={{ color: '#111827' }}>{rowCount}</strong> results
      </Typography>
      <GridPagination />
    </GridFooterContainer>
  );
}

const meetingsTableSx = {
  '& .MuiDataGrid-row': { cursor: 'default' },
  border: 'none',
  overflow: 'hidden',
  width: '100%',
  '& .MuiDataGrid-virtualScroller': {
    overflowY: 'auto',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: 'rgba(249, 250, 251, 0.8)',
    borderBottom: '1px solid #e5e7eb',
    '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox)': {
      px: '16px',
      py: '12px',
    },
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    textTransform: 'uppercase',
    fontSize: '0.7rem',
    letterSpacing: '0.05em',
    fontWeight: 600,
    color: '#6b7280',
  },
  '& .MuiDataGrid-cell:not(.MuiDataGrid-cellCheckbox)': {
    px: '16px',
    py: '12px',
  },
  '& .MuiDataGrid-cellCheckbox': {
    py: '12px',
    pl: 0.5,
    pr: 1.5,
    justifyContent: 'center',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(249, 250, 251, 0.8)',
  },
  '& .MuiDataGrid-footerContainer': {
    overflow: 'hidden',
  },
  '& .MuiTablePagination-displayedRows': {
    display: 'none',
  },
  '& .MuiDataGrid-columnHeaderCheckbox': {
    py: '12px',
    pl: 0.5,
    pr: 1.5,
    justifyContent: 'center',
  },
};

function formatClientIdFallback(row: DeletedDriveFolderRow): string {
  const raw = row.prospoContext?.clientId as unknown;
  if (raw == null || raw === '') return '—';
  if (typeof raw === 'object' && raw !== null && '$oid' in (raw as Record<string, unknown>)) {
    return String((raw as { $oid: string }).$oid);
  }
  return String(raw);
}

function displayOrg(row: DeletedDriveFolderRow): string {
  const label = row.orgName?.trim();
  if (label) return label;
  const id = row.prospoContext?.orgId;
  if (id == null || id === '') return '—';
  return String(id);
}

function displayClient(row: DeletedDriveFolderRow): string {
  if (row.clientName != null && row.clientName !== '') {
    return row.clientName.trim();
  }
  return formatClientIdFallback(row);
}

interface Props {
  rows: DeletedDriveFolderRow[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  rowSelectionModel: GridRowSelectionModel;
  onRowSelectionModelChange: (model: GridRowSelectionModel) => void;
  onPaginationModelChange: (model: GridPaginationModel) => void;
}

const buildColumns = (): GridColDef<DeletedDriveFolderRow>[] => [
  {
    field: 'updatedAt',
    headerName: 'Deleted At',
    minWidth: 170,
    flex: 0.9,
    align: 'left',
    headerAlign: 'left',
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
          {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'name',
    headerName: 'Folder Name',
    flex: 1,
    minWidth: 140,
    align: 'left',
    headerAlign: 'left',
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', maxWidth: '100%' }}>
        <Typography
          sx={{
            fontWeight: 500,
            color: '#111827',
            fontSize: '0.875rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.name}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'path',
    headerName: 'Path',
    flex: 1,
    minWidth: 160,
    align: 'left',
    headerAlign: 'left',
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', maxWidth: '100%' }}>
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: '#6b7280',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.path ?? '—'}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'orgId',
    headerName: 'Organisation',
    minWidth: 180,
    flex: 0.85,
    align: 'left',
    headerAlign: 'left',
    valueGetter: (_v, row) => displayOrg(row),
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', maxWidth: '100%' }}>
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: '#6b7280',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayOrg(row)}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'clientId',
    headerName: 'Client',
    minWidth: 160,
    flex: 0.75,
    align: 'left',
    headerAlign: 'left',
    sortable: false,
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', maxWidth: '100%' }}>
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: '#6b7280',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayClient(row)}
        </Typography>
      </Box>
    ),
  },
  {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    flex: 0,
    width: 56,
    minWidth: 56,
    maxWidth: 56,
  },
];

export default function DeletedFoldersTable({
  rows,
  total,
  page,
  pageSize,
  loading,
  rowSelectionModel,
  onRowSelectionModelChange,
  onPaginationModelChange,
}: Props) {
  return (
    <Box sx={{ flexGrow: 1, width: '100%', overflow: 'hidden', minHeight: 0 }}>
      <DataGrid
        rows={rows}
        columns={buildColumns()}
        getRowId={(r) => String(r._id)}
        rowCount={total}
        paginationMode="server"
        paginationModel={{ page: page - 1, pageSize }}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[5, 10, 20, 50]}
        loading={loading}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        disableRowSelectionOnClick
        rowHeight={48}
        slots={{ footer: RecycleBinFoldersTableFooter }}
        hideFooterSelectedRowCount
        sx={{
          ...meetingsTableSx,
          height: '100%',
          minHeight: { xs: 420, lg: 'calc(100vh - 320px)' },
        }}
      />
    </Box>
  );
}
